/**
 * Email Marketing recipes + campaign blocks — PURE helpers.
 *
 * The Omnisend/HubSpot-style layer over the existing sequence engine:
 *
 *   1. Automation RECIPES — pre-built, governed cadences (Welcome,
 *      Re-engage lost lead, Renewal reminder, Weekly customer update)
 *      that map straight onto `Sequence` steps from src/lib/crm/outreach.ts.
 *      A recipe never sends anything: `recipeToSequenceInput` produces the
 *      exact shape `saveSequence` persists, and enrollment still flows
 *      through the blast-radius preview + vetoed approval batch.
 *
 *   2. Campaign BLOCKS — a tiny block model (heading/paragraph/button/
 *      divider) with deterministic text+HTML renderers and per-contact
 *      token substitution, so a one-off campaign compiles into governed,
 *      approvable send diffs — never a direct outbox write.
 *
 * Deliberately PURE: no store access, no clocks, no network. Everything
 * here is a mapping the UI (and tests) can trust byte-for-byte.
 */

import type { SequenceStep } from './outreach';

// ---------------------------------------------------------------------------
// Automation recipes
// ---------------------------------------------------------------------------

/** Built-in CRM audiences a recipe targets — resolved against live people. */
export type RecipeAudience = 'prospects' | 'customers' | 'closed-lost';

export interface AutomationRecipe {
  id: string;
  name: string;
  /** One-line pitch shown on the gallery card. */
  tagline: string;
  /** The default CRM audience the recipe enrolls. */
  audience: RecipeAudience;
  /** Ordered, governed cadence steps (same model the engine schedules). */
  steps: SequenceStep[];
}

export const RECIPE_AUDIENCE_LABELS: Record<RecipeAudience, string> = {
  prospects: 'All prospects',
  customers: 'All customers',
  'closed-lost': 'Closed-lost prospects',
};

/** Prefix stamped onto sequences a recipe creates — makes provenance greppable. */
export const RECIPE_SEQUENCE_PREFIX = 'Recipe:';

export const AUTOMATION_RECIPES: AutomationRecipe[] = [
  {
    id: 'welcome',
    name: 'Welcome series',
    tagline: 'Greet new contacts with a 3-touch onboarding cadence.',
    audience: 'prospects',
    steps: [
      {
        id: 'welcome-1',
        dayOffset: 0,
        channel: 'email',
        subject: 'Welcome aboard, {{firstName}}',
        body: 'Hi {{firstName}},\n\nGreat to have {{company}} with us. Here’s what to expect in your first week, plus who to reach if anything comes up.\n\n— The team',
      },
      {
        id: 'welcome-2',
        dayOffset: 2,
        channel: 'email',
        subject: 'Getting the most out of week one',
        body: 'Hi {{firstName}},\n\nA quick tour of the three things teams like {{company}} set up first — each takes under ten minutes.',
      },
      {
        id: 'welcome-3',
        dayOffset: 7,
        channel: 'email',
        subject: 'How’s it going so far?',
        body: 'Hi {{firstName}},\n\nOne week in — anything unclear? Reply here and a human (not a bot) will answer.',
      },
    ],
  },
  {
    id: 're-engage-lost',
    name: 'Re-engage lost lead',
    tagline: 'Respectfully reopen the conversation with closed-lost deals.',
    audience: 'closed-lost',
    steps: [
      {
        id: 'reengage-1',
        dayOffset: 0,
        channel: 'email',
        subject: 'Circling back, {{firstName}}',
        body: 'Hi {{firstName}},\n\nWhen we last spoke the timing wasn’t right for {{company}}. A lot has shipped since — worth a fresh 15-minute look?',
      },
      {
        id: 'reengage-2',
        dayOffset: 5,
        channel: 'email',
        subject: 'What changed since we talked',
        body: 'Hi {{firstName}},\n\nShort version: the two gaps you flagged are closed. Happy to show exactly how in a quick walkthrough.',
      },
      {
        id: 'reengage-3',
        dayOffset: 12,
        channel: 'email',
        subject: 'Last note from me',
        body: 'Hi {{firstName}},\n\nI’ll close the loop here — if priorities shift at {{company}}, this thread is the fastest way back in.',
      },
    ],
  },
  {
    id: 'renewal-reminder',
    name: 'Renewal reminder',
    tagline: 'Nudge customers ahead of renewal with value recaps.',
    audience: 'customers',
    steps: [
      {
        id: 'renewal-1',
        dayOffset: 0,
        channel: 'email',
        subject: 'Your renewal is coming up',
        body: 'Hi {{firstName}},\n\n{{company}}’s renewal window opens soon. Here’s a recap of what your team shipped with us this cycle.',
      },
      {
        id: 'renewal-2',
        dayOffset: 7,
        channel: 'email',
        subject: 'Renewal details + next steps',
        body: 'Hi {{firstName}},\n\nEverything you need to renew in one place — terms, seats, and a direct line to your account owner for questions.',
      },
      {
        id: 'renewal-3',
        dayOffset: 14,
        channel: 'email',
        subject: 'Anything blocking renewal?',
        body: 'Hi {{firstName}},\n\nA quick check-in before the window closes for {{company}} — if there’s a question or a change of plans, reply here and your account owner will sort it.',
      },
    ],
  },
  {
    id: 'weekly-update',
    name: 'Weekly customer update',
    tagline: 'A steady weekly digest for your customer base.',
    audience: 'customers',
    steps: [
      {
        id: 'weekly-1',
        dayOffset: 0,
        channel: 'email',
        subject: 'This week at a glance',
        body: 'Hi {{firstName}},\n\nWhat shipped, what’s next, and one tip your team can use today.',
      },
      {
        id: 'weekly-2',
        dayOffset: 7,
        channel: 'email',
        subject: 'This week at a glance',
        body: 'Hi {{firstName}},\n\nWeek two digest: highlights, fixes, and what’s rolling out next.',
      },
      {
        id: 'weekly-3',
        dayOffset: 14,
        channel: 'email',
        subject: 'This week at a glance',
        body: 'Hi {{firstName}},\n\nWeek three digest — plus a quick pulse-check: is this cadence useful? Reply and tell us.',
      },
      {
        id: 'weekly-4',
        dayOffset: 21,
        channel: 'email',
        subject: 'This week at a glance',
        body: 'Hi {{firstName}},\n\nWeek four digest. From here the update continues only if your team finds it valuable.',
      },
    ],
  },
];

/** Exactly what `saveSequence` consumes — name + steps, ids derived there. */
export interface RecipeSequenceInput {
  name: string;
  steps: SequenceStep[];
}

/**
 * Recipe → sequence-engine input. The name is prefixed so recipe-born
 * sequences are self-identifying in the picker, and steps are re-sorted
 * by dayOffset defensively so scheduling order never depends on
 * declaration order.
 */
export function recipeToSequenceInput(recipe: AutomationRecipe): RecipeSequenceInput {
  return {
    name: `${RECIPE_SEQUENCE_PREFIX} ${recipe.name}`,
    steps: [...recipe.steps].sort((a, b) => a.dayOffset - b.dayOffset),
  };
}

/** The minimal person shape the audience filter + token renderer read. */
export interface RecipeContact {
  id: string;
  name: string;
  email?: string;
  company?: string;
  category?: string;
  stage?: string;
}

/** Resolve a recipe audience against live CRM people — a pure filter. */
export function filterRecipeAudience<T extends RecipeContact>(
  audience: RecipeAudience,
  people: T[],
): T[] {
  switch (audience) {
    case 'prospects':
      return people.filter((p) => p.category === 'prospect');
    case 'customers':
      return people.filter((p) => p.category === 'customer');
    case 'closed-lost':
      return people.filter((p) => p.category === 'prospect' && p.stage === 'closed-lost');
  }
}

// ---------------------------------------------------------------------------
// Campaign blocks
// ---------------------------------------------------------------------------

export type CampaignBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'button'; label: string; url: string }
  | { kind: 'divider' };

/** Per-contact token substitution: {{name}}, {{firstName}}, {{company}}. */
export function applyTokens(text: string, contact: RecipeContact): string {
  const firstName = contact.name.trim().split(/\s+/)[0] ?? contact.name;
  return text
    .replace(/\{\{\s*firstName\s*\}\}/g, firstName)
    .replace(/\{\{\s*name\s*\}\}/g, contact.name)
    .replace(/\{\{\s*company\s*\}\}/g, contact.company ?? 'your team');
}

/** Deterministic plain-text render — what the diff/ledger records. */
export function renderBlocksText(blocks: CampaignBlock[]): string {
  return blocks
    .map((b) => {
      switch (b.kind) {
        case 'heading':
          return b.text.toUpperCase();
        case 'paragraph':
          return b.text;
        case 'button':
          return `${b.label}: ${b.url}`;
        case 'divider':
          return '---';
      }
    })
    .join('\n\n');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Deterministic HTML render — every text node escaped, nothing injected. */
export function renderBlocksHtml(blocks: CampaignBlock[]): string {
  return blocks
    .map((b) => {
      switch (b.kind) {
        case 'heading':
          return `<h2>${escapeHtml(b.text)}</h2>`;
        case 'paragraph':
          return `<p>${escapeHtml(b.text)}</p>`;
        case 'button':
          return `<a href="${escapeHtml(b.url)}">${escapeHtml(b.label)}</a>`;
        case 'divider':
          return '<hr />';
      }
    })
    .join('\n');
}

/** Human-readable problems that block a campaign from being queued. */
export function validateCampaign(subject: string, blocks: CampaignBlock[]): string[] {
  const issues: string[] = [];
  if (subject.trim() === '') issues.push('Subject is empty.');
  const content = blocks.filter((b) => b.kind !== 'divider');
  if (content.length === 0) issues.push('Add at least one content block.');
  for (const b of blocks) {
    if ((b.kind === 'heading' || b.kind === 'paragraph') && b.text.trim() === '') {
      issues.push(`Empty ${b.kind} block.`);
    }
    if (b.kind === 'button' && (b.label.trim() === '' || b.url.trim() === '')) {
      issues.push('Button block needs both a label and a URL.');
    }
  }
  return issues;
}

/** One governed, approvable campaign send — never a direct outbox write. */
export interface CampaignSendDiff {
  contactId: string;
  recipient: string;
  subject: string;
  text: string;
  html: string;
}

/**
 * Compile a campaign into per-contact send diffs. Contacts without an
 * email address are honestly skipped (never guessed); tokens resolve
 * per contact in both subject and body.
 */
export function buildCampaignSendDiffs(
  subject: string,
  blocks: CampaignBlock[],
  contacts: RecipeContact[],
): CampaignSendDiff[] {
  return contacts
    .filter((c) => (c.email ?? '').trim() !== '')
    .map((c) => {
      const personalized = blocks.map<CampaignBlock>((b) => {
        switch (b.kind) {
          case 'heading':
            return { kind: 'heading', text: applyTokens(b.text, c) };
          case 'paragraph':
            return { kind: 'paragraph', text: applyTokens(b.text, c) };
          case 'button':
            return { kind: 'button', label: applyTokens(b.label, c), url: b.url };
          case 'divider':
            return b;
        }
      });
      return {
        contactId: c.id,
        recipient: (c.email ?? '').trim(),
        subject: applyTokens(subject, c),
        text: renderBlocksText(personalized),
        html: renderBlocksHtml(personalized),
      };
    });
}
